// ===================================================================
// Time & Labor — Module Layout with nav bar
// FEMA Force Account Labor Tracking
// ===================================================================

import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Group, Button, Divider } from '@mantine/core';
import {
  IconLayoutDashboard, IconClock, IconUsers,
  IconFileSpreadsheet, IconReportAnalytics,
  IconChecklist, IconUpload, IconSettings,
} from '@tabler/icons-react';
import { useFilteredNav } from '../../hooks/useFilteredNav';
import { useRoleStore } from '../../store/roleStore';
import type { NavItem } from '../../types/nav';
import MyTimePortal from './MyTimePortal';
import Breadcrumbs from '../../components/ui/Breadcrumbs';

const TEAL = '#0D9488';

// Roles that get the personal portal instead of admin view
const PORTAL_SLUGS = ['timelabor_only'];

const NAV_ITEMS: NavItem[] = [
  { label: 'Dashboard',  path: '/timelabor',            icon: IconLayoutDashboard, exact: true },
  { label: 'Time Entry', path: '/timelabor/timeentry',   icon: IconClock },
  { label: 'Employees',  path: '/timelabor/employees',   icon: IconUsers },
  { label: 'Review',     path: '/timelabor/review',      icon: IconChecklist, minLevel: 'admin' },
  { label: 'Import',     path: '/timelabor/import',      icon: IconUpload, minLevel: 'admin' },
  { label: 'Summaries',  path: '/timelabor/summaries',   icon: IconFileSpreadsheet, minLevel: 'admin' },
  { label: 'FF-104',     path: '/timelabor/ff104',        icon: IconFileSpreadsheet, minLevel: 'admin' },
  { label: 'Reports',    path: '/timelabor/reports',      icon: IconReportAnalytics, minLevel: 'admin' },
  { label: 'Settings',   path: '/timelabor/settings',     icon: IconSettings, minLevel: 'admin' },
];

export default function TimeLaborLayout() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const effectiveRole = useRoleStore(s => s.demoRoleOverride ?? s.myRole);
  const slug = effectiveRole?.roleSlug ?? '';
  const visibleItems = useFilteredNav('timelabor', NAV_ITEMS);

  // Time & Labor only users get the personal portal
  if (PORTAL_SLUGS.includes(slug)) {
    return (
      <div style={{ padding: '24px' }}>
        <MyTimePortal />
      </div>
    );
  }

  const currentPage = NAV_ITEMS.find(item =>
    item.exact ? pathname === item.path : pathname.startsWith(item.path)
  );

  return (
    <div style={{ padding: '24px' }}>
      <Breadcrumbs
        items={[
          { label: 'Hub', to: '/' },
          { label: 'Time & Labor', to: '/timelabor' },
          ...(currentPage && currentPage.path !== '/timelabor' ? [{ label: currentPage.label }] : []),
        ]}
      />
      <Group gap={6} mb="md" style={{ borderBottom: '1px solid var(--frost)', paddingBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        {visibleItems.map((item, i) => {
          const active = item.exact
            ? pathname === item.path
            : pathname.startsWith(item.path);
          return (
            <span key={item.path} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              {i > 0 && i === 3 && <Divider orientation="vertical" style={{ height: 20, margin: '0 4px' }} />}
              <Button
                variant={active ? 'filled' : 'light'}
                color={active ? TEAL : 'gray'}
                size="xs"
                leftSection={<item.icon size={14} />}
                onClick={() => navigate(item.path)}
              >
                {item.label}
              </Button>
            </span>
          );
        })}
      </Group>
      <Outlet />
    </div>
  );
}
